import React, { useState } from "react";
import * as dmsService from "../../../services/dmsService";
import * as finService from "../../../services/finService";
import useFinOptions from "../../Fin/useFinOptions";
import useDmsOptions from "../useDmsOptions";
import { fmtMoney } from "../../Fin/gl/glContext";
import { DataTable, hint, linkBtn } from "../../Fin/gl/glUi";
import { ActionHistory, DmsBadge, DocHead, Panel, Section, run } from "../sales/dmsUi";
import { AppDropdown, EmptyState, Field, FormDrawer, LoadState, PageHeader, PrimaryButton, SecondaryButton, formatDate, inputStyle, pageStyle, useLoad } from "../../../components/ui";

// Body Shop: damage estimates by panel (body labour, paint, parts, sublet), the insurer's claim
// (approved amount and the customer's excess), then a body shop repair order whose invoice is split
// between the insurer and the customer.

const KINDS = [
  { label: "Body labour (hours)", value: "LABOUR" },
  { label: "Paint (panel)", value: "PAINT" },
  { label: "Part", value: "PART" },
  { label: "Sublet", value: "SUBLET" },
];
const OPS = ["REPAIR", "REPLACE", "PAINT", "BLEND", "R&I"].map((v) => ({ label: v, value: v }));
const blank = () => ({ line_kind: "LABOUR", damage_area: "", operation: "REPAIR", description: "", quantity: 1, unit_price: "" });

function EstimateEditor({ onHide, onDone }) {
  const fin = useFinOptions(["locations"]);
  const opts = useDmsOptions(["insurers"]);
  const customers = useLoad(() => finService.listCustomers({ size: 300 }), []);
  const [h, setH] = useState({ location_id: null, customer_account_id: null, plate_no: "", vin: "", vehicle_desc: "", insurer_id: null, damage_notes: "" });
  const [lines, setLines] = useState([blank()]);
  const [busy, setBusy] = useState(false);
  const loc = h.location_id || (fin.locations && fin.locations[0] ? fin.locations[0].value : null);
  const set = (i, patch) => setLines(lines.map((l, k) => (k === i ? { ...l, ...patch } : l)));
  const total = lines.reduce((s, l) => s + Number(l.quantity || 0) * Number(l.unit_price || 0), 0);
  const save = async () => {
    setBusy(true);
    const res = await run(() => dmsService.saveEstimate(null, { ...h, location_id: loc, insurer_id: h.insurer_id || undefined, lines: lines.map((l) => ({ ...l, quantity: Number(l.quantity), unit_price: Number(l.unit_price) })) }));
    setBusy(false);
    if (res) {
      onDone(res.data.id);
      onHide();
    }
  };
  return (
    <Panel title="New body shop estimate" onHide={() => !busy && onHide()} width={1000} footer={<><SecondaryButton onClick={onHide} disabled={busy}>Cancel</SecondaryButton><PrimaryButton onClick={save} disabled={busy}>Save estimate</PrimaryButton></>}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "0 12px" }}>
        <Field label="Customer" required>
          <AppDropdown id="be-cust" value={h.customer_account_id} filter options={(customers.data || []).map((c) => ({ label: `${c.name} (${c.account_number})`, value: c.id }))} onChange={(e) => setH({ ...h, customer_account_id: e.value })} />
        </Field>
        <Field label="Insurer (blank: customer pays)">
          <AppDropdown id="be-ins" value={h.insurer_id} options={[{ label: "Customer pays", value: null }, ...(opts.insurers || [])]} onChange={(e) => setH({ ...h, insurer_id: e.value })} />
        </Field>
        <Field label="Workshop">
          <AppDropdown id="be-loc" value={loc} options={fin.locations || []} onChange={(e) => setH({ ...h, location_id: e.value })} />
        </Field>
        <Field label="Plate">
          <input className="form-control" style={inputStyle} value={h.plate_no} onChange={(e) => setH({ ...h, plate_no: e.target.value })} />
        </Field>
        <Field label="VIN">
          <input className="form-control" style={inputStyle} maxLength={17} value={h.vin} onChange={(e) => setH({ ...h, vin: e.target.value.toUpperCase() })} />
        </Field>
        <Field label="Vehicle">
          <input className="form-control" style={inputStyle} value={h.vehicle_desc} onChange={(e) => setH({ ...h, vehicle_desc: e.target.value })} />
        </Field>
      </div>
      <Field label="Damage notes">
        <textarea className="form-control" rows={2} value={h.damage_notes} onChange={(e) => setH({ ...h, damage_notes: e.target.value })} />
      </Field>
      {lines.map((l, i) => (
        <div key={i} className="d-flex flex-wrap gap-2 align-items-center mb-2">
          <div style={{ width: 170 }}>
            <AppDropdown id={`be-k-${i}`} value={l.line_kind} options={KINDS} onChange={(e) => set(i, { line_kind: e.value })} />
          </div>
          <input className="form-control" style={{ ...inputStyle, width: 150 }} placeholder="Panel / area" aria-label="Panel or area" value={l.damage_area} onChange={(e) => set(i, { damage_area: e.target.value })} />
          <div style={{ width: 120 }}>
            <AppDropdown id={`be-o-${i}`} value={l.operation} options={OPS} onChange={(e) => set(i, { operation: e.value })} />
          </div>
          <input className="form-control" style={{ ...inputStyle, flex: 1, minWidth: 180 }} placeholder="Description" aria-label="Description" value={l.description} onChange={(e) => set(i, { description: e.target.value })} />
          <input type="number" step="any" min={0} className="form-control" style={{ ...inputStyle, width: 90 }} aria-label={l.line_kind === "LABOUR" ? "Hours" : "Quantity"} title={l.line_kind === "LABOUR" ? "Hours" : "Quantity"} value={l.quantity} onChange={(e) => set(i, { quantity: e.target.value })} />
          <input type="number" step="any" min={0} className="form-control" style={{ ...inputStyle, width: 110 }} placeholder={l.line_kind === "LABOUR" ? "Rate" : "Price"} aria-label="Price" value={l.unit_price} onChange={(e) => set(i, { unit_price: e.target.value })} />
          {lines.length > 1 ? (
            <button type="button" style={linkBtn} onClick={() => setLines(lines.filter((_, k) => k !== i))}>
              Remove
            </button>
          ) : null}
        </div>
      ))}
      <div className="d-flex justify-content-between align-items-center">
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setLines([...lines, blank()])}>
          Add line
        </button>
        <b>Estimate {fmtMoney(total)} before VAT</b>
      </div>
    </Panel>
  );
}

function EstimateView({ id, onHide, onChanged }) {
  const { data: e, loading, error, reload } = useLoad(() => dmsService.getEstimate(id), [id]);
  const [drawer, setDrawer] = useState(null);
  const refresh = () => {
    reload();
    onChanged();
  };
  const st = e ? e.status : null;
  const fields = {
    claim: [
      { key: "claim_number", label: "Insurer's claim number", required: true, max: 60 },
      { key: "policy_number", label: "Policy number", max: 60 },
      { key: "surveyor_name", label: "Surveyor", max: 150 },
      { key: "excess_amount", label: "Customer's excess (before VAT)", type: "number", step: "any", min: 0 },
    ],
    "insurer-decision": [
      { key: "approved_amount", label: "Amount approved (before VAT; 0 = rejected)", type: "number", step: "any", min: 0, required: true },
      { key: "excess_amount", label: "Customer's excess", type: "number", step: "any", min: 0 },
      { key: "surveyor_name", label: "Surveyor", max: 150 },
    ],
    convert: [
      { key: "odometer_in", label: "Odometer (km)", type: "number", min: 0 },
      { key: "promised_at", label: "Promised for", type: "datetime-local" },
    ],
  };
  const num = (v) => (v === undefined || v === "" || v === null ? undefined : Number(v));
  return (
    <Panel title={e ? `Estimate ${e.estimate_number}` : "Estimate"} onHide={onHide} width={940}>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {e ? (
          <>
            <DocHead title="" number={`${e.customer_name}${e.plate_no ? ` · ${e.plate_no}` : ""}`} status={st} sub={`${e.vehicle_desc || ""} · ${e.insurer_name ? `insurer ${e.insurer_name}` : "customer pays"}${e.ro ? ` · ${e.ro.ro_number}` : ""}`}>
              {st === "DRAFT" && e.insurer_name ? <PrimaryButton onClick={() => setDrawer("claim")}>Send to insurer</PrimaryButton> : null}
              {st === "DRAFT" && !e.insurer_name ? <PrimaryButton onClick={() => run(() => dmsService.estimateAction(id, "approve"), refresh)}>Customer approved</PrimaryButton> : null}
              {st === "SUBMITTED" ? <PrimaryButton onClick={() => setDrawer("insurer-decision")}>Insurer's decision</PrimaryButton> : null}
              {st === "APPROVED" ? <PrimaryButton onClick={() => setDrawer("convert")}>Open repair order</PrimaryButton> : null}
            </DocHead>
            <Section title="Repair">
              <DataTable
                rows={e.lines}
                rowKey={(l) => l.line_no}
                columns={[
                  { label: "Area", render: (l) => l.damage_area || "" },
                  { label: "Operation", render: (l) => l.operation || "" },
                  { label: "Work", render: (l) => `${l.line_kind === "LABOUR" ? "Labour" : l.line_kind === "PAINT" ? "Paint" : l.line_kind === "PART" ? "Part" : "Sublet"}: ${l.description}` },
                  { label: "Qty / hrs", num: true, render: (l) => Number(l.quantity) },
                  { label: "Price", num: true, render: (l) => fmtMoney(l.unit_price) },
                  { label: "Amount", num: true, render: (l) => fmtMoney(l.amount) },
                ]}
              />
              <div style={{ textAlign: "right", marginTop: 6, fontSize: 14 }}>
                Labour {fmtMoney(e.labour_total)} · paint {fmtMoney(e.paint_total)} · parts {fmtMoney(e.parts_total)} · <b>total {fmtMoney(e.total)}</b> before VAT
              </div>
            </Section>
            {e.claim ? (
              <Section title="Insurance claim">
                <div style={{ fontSize: 14 }}>
                  Claim <b>{e.claim.claim_number}</b> {e.claim.policy_number ? `· policy ${e.claim.policy_number}` : ""} {e.claim.surveyor_name ? `· surveyor ${e.claim.surveyor_name}` : ""} · <DmsBadge status={e.claim.status} />
                  <div>
                    Claimed {fmtMoney(e.claim.claimed_amount)} · approved {e.claim.approved_amount === null ? "—" : fmtMoney(e.claim.approved_amount)} · customer's excess {fmtMoney(e.claim.excess_amount)}
                  </div>
                </div>
              </Section>
            ) : null}
            <Section title="Action history">
              <ActionHistory actions={e.actions} />
            </Section>
          </>
        ) : null}
      </LoadState>
      <FormDrawer
        show={!!drawer}
        title={drawer === "claim" ? "Send to the insurer" : drawer === "convert" ? "Open the repair order" : "Insurer's decision"}
        initial={drawer === "insurer-decision" && e ? { approved_amount: Number(e.total), excess_amount: e.claim ? Number(e.claim.excess_amount) : 0 } : {}}
        onHide={() => setDrawer(null)}
        onDone={refresh}
        onSave={(clean) => dmsService.estimateAction(id, drawer, { ...clean, excess_amount: num(clean.excess_amount), approved_amount: num(clean.approved_amount), odometer_in: num(clean.odometer_in) })}
        fields={drawer ? fields[drawer] : []}
      />
    </Panel>
  );
}

export default function BodyShop() {
  const [q, setQ] = useState("");
  const { data, loading, error, reload } = useLoad(() => dmsService.listEstimates({ q: q || undefined }), [q]);
  const [open, setOpen] = useState(null);
  const [creating, setCreating] = useState(false);
  return (
    <div style={pageStyle}>
      <PageHeader title="Body Shop" subtitle="Damage estimates, insurance claims and body repairs" actions={<PrimaryButton onClick={() => setCreating(true)}>New estimate</PrimaryButton>} />
      <input className="form-control mb-3" style={{ ...inputStyle, maxWidth: 300 }} placeholder="Estimate, customer, plate, claim" aria-label="Search estimates" value={q} onChange={(e) => setQ(e.target.value)} />
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          onRowClick={(r) => setOpen(r.id)}
          empty={<EmptyState title="No estimates" />}
          columns={[
            { label: "Estimate", render: (r) => <b>{r.estimate_number}</b> },
            { label: "Date", render: (r) => formatDate(r.created_at) },
            { label: "Customer", render: (r) => r.customer_name },
            { label: "Vehicle", render: (r) => `${r.plate_no || ""} ${r.vehicle_desc || ""}`.trim() },
            { label: "Insurer / claim", render: (r) => (r.insurer_name ? `${r.insurer_name} ${r.claim_number || ""}` : "Customer") },
            { label: "Total", num: true, render: (r) => fmtMoney(r.total) },
            { label: "Status", render: (r) => <DmsBadge status={r.status} /> },
          ]}
        />
        <div style={{ ...hint, marginTop: 6 }}>Approved estimates become BODYSHOP repair orders (Repair Orders); their invoice is split between the insurer and the customer.</div>
      </LoadState>
      {open ? <EstimateView id={open} onHide={() => setOpen(null)} onChanged={reload} /> : null}
      {creating ? <EstimateEditor onHide={() => setCreating(false)} onDone={(id) => {
            reload();
            setOpen(id);
          }} /> : null}
    </div>
  );
}
